import { ACCESS_PERMISSION_HIERARCHY } from "@/constants/accessPermissionHierarchy"

const fail = (message) => {
  throw new Error(`Access permission hierarchy: ${message}`)
}

const uniqueCodes = (rows) => {
  const codes = rows.map((row) => String(row?.code || "").trim())
  if (codes.some((code) => !code))
    fail("catalogue drift: capability code is empty")
  if (new Set(codes).size !== codes.length)
    fail("catalogue drift: duplicate capability code")
  return codes
}

const isAncestor = (ancestorId, descendantId, parents) => {
  let current = parents.get(descendantId)
  while (current) {
    if (current === ancestorId) return true
    current = parents.get(current)
  }
  return false
}

export const projectAccessPermissionHierarchy = (
  catalogue,
  registry = ACCESS_PERMISSION_HIERARCHY
) => {
  const capabilities = Array.isArray(catalogue?.capabilities)
    ? catalogue.capabilities
    : []
  const runtimeOnly = Array.isArray(catalogue?.runtime_only)
    ? catalogue.runtime_only
    : []
  const nodes = Array.isArray(registry?.nodes) ? registry.nodes : []
  const rights = Array.isArray(registry?.rights) ? registry.rights : []

  const capabilityCodes = uniqueCodes(capabilities)
  const rightCodes = uniqueCodes(rights)
  const runtimeOnlyCodes = new Set(uniqueCodes(runtimeOnly))

  const nodeById = new Map()
  const parents = new Map()
  nodes.forEach((node) => {
    const id = String(node?.id || "").trim()
    if (!id || nodeById.has(id))
      fail("registry contains an empty or duplicate node id")
    nodeById.set(id, node)
    parents.set(id, node.parentId || null)
  })
  nodes.forEach((node) => {
    if (node.parentId && !nodeById.has(node.parentId)) {
      fail(`registry node ${node.id} references a missing parent`)
    }
  })

  const backendByCode = new Map(capabilities.map((row) => [row.code, row]))

  rights.forEach((right) => {
    if (runtimeOnlyCodes.has(right.code)) {
      fail(`runtime-only capability ${right.code} cannot be placed`)
    }
    if (!["read", "edit"].includes(right.class)) {
      fail(`unclassified capability ${right.code}`)
    }
    if (!Array.isArray(right.placements) || right.placements.length === 0) {
      fail(`unclassified capability ${right.code} has no menu placement`)
    }
    if (new Set(right.placements).size !== right.placements.length) {
      fail(`duplicate placement for capability ${right.code}`)
    }
    right.placements.forEach((nodeId) => {
      if (!nodeById.has(nodeId))
        fail(`capability ${right.code} references missing node ${nodeId}`)
    })
    for (let left = 0; left < right.placements.length; left += 1) {
      for (
        let rightIndex = left + 1;
        rightIndex < right.placements.length;
        rightIndex += 1
      ) {
        const first = right.placements[left]
        const second = right.placements[rightIndex]
        if (
          isAncestor(first, second, parents) ||
          isAncestor(second, first, parents)
        ) {
          fail(`ancestor-descendant duplicate for capability ${right.code}`)
        }
      }
    }
    if (backendByCode.get(right.code)?.module_scoped === true) {
      right.placements.forEach((nodeId) => {
        if (nodeId !== "modules:general" && !/^module:\d+$/.test(nodeId)) {
          fail(
            `module-scoped capability ${right.code} must be placed on modules:general or a module node, found ${nodeId}`
          )
        }
      })
    }
  })

  const backendSet = new Set(capabilityCodes)
  const registrySet = new Set(rightCodes)
  const added = capabilityCodes.filter((code) => !registrySet.has(code))
  const removed = rightCodes.filter((code) => !backendSet.has(code))
  if (added.length || removed.length) {
    fail(
      `catalogue drift: added=[${added.join(",")}], removed=[${removed.join(",")}]`
    )
  }

  const projectedByCode = new Map()
  const aliasesByCode = {}
  const groupPlacementNodeIds = new Map()
  const nodeProjection = new Map(
    nodes.map((node) => [
      node.id,
      {
        ...node,
        children: [],
        capabilities: [],
      },
    ])
  )

  const addGroupNode = (groupCode, nodeId) => {
    if (!groupCode) return
    if (!groupPlacementNodeIds.has(groupCode))
      groupPlacementNodeIds.set(groupCode, new Set())
    groupPlacementNodeIds.get(groupCode).add(nodeId)
  }

  rights.forEach((right) => {
    const backend = backendByCode.get(right.code)
    const capability = Object.freeze({
      ...backend,
      accessClass: right.class,
      decisionIdentity: right.code,
    })
    projectedByCode.set(right.code, capability)
    const aliases = []
    const seenNodeIds = new Set()
    const pushAlias = (nodeId, decisionKind, moduleId) => {
      if (!nodeById.has(nodeId) || seenNodeIds.has(nodeId)) return
      seenNodeIds.add(nodeId)
      aliases.push(
        Object.freeze({ nodeId, capability, decisionKind, moduleId })
      )
      nodeProjection
        .get(nodeId)
        .capabilities.push(
          Object.freeze({ ...capability, decisionKind, moduleId })
        )
      addGroupNode(backend?.group_code, nodeId)
    }
    right.placements.forEach((nodeId) => {
      const placedNode = nodeById.get(nodeId)
      const isModuleNode =
        placedNode?.kind === "module" && placedNode.moduleId != null
      pushAlias(
        nodeId,
        isModuleNode ? "module" : "global",
        isModuleNode ? placedNode.moduleId : undefined
      )
    })
    aliasesByCode[right.code] = aliases
  })

  const roots = []
  nodes.forEach((node) => {
    const projected = nodeProjection.get(node.id)
    if (node.parentId)
      nodeProjection.get(node.parentId).children.push(projected)
    else roots.push(projected)
  })
  const sections =
    roots.length === 1 &&
    roots[0].children.every((node) => node.kind === "section")
      ? roots[0].children
      : roots

  return {
    sections,
    capabilities: rights.map((right) => projectedByCode.get(right.code)),
    aliasesByCode,
    groupPlacementNodeIds,
    counts: {
      assignable: rights.length,
      read: rights.filter((right) => right.class === "read").length,
      edit: rights.filter((right) => right.class === "edit").length,
    },
  }
}
